// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/OrdemServico.tsx
//  Consulta de Ordens de Serviço (OS). Busca, status, período, cliente,
//  vendedor, técnico, status do cadastro, prioridade, ordenação e KPIs
//  rodam NO SERVIDOR.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Wrench, Clock, CheckCircle2,
  Eye, Pencil, ChevronLeft, ChevronRight, PlayCircle,
} from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { BotaoExportar } from '@/components/BotaoExportar';
import { PessoaLookupCombobox } from '@/components/PessoaLookupCombobox';
import { VendedorSearchCombobox } from '@/components/VendedorSearchCombobox';
import { ColaboradorSearchCombobox } from '@/components/ColaboradorSearchCombobox';
import { useOrdemServico } from '@/features/operacional/hooks/useOrdemServico';
import { useStatusOsCombo } from '@/features/operacional/hooks/useStatusOs';
import { calcularStatusOs } from '@/features/operacional/types';
import type { StatusOs } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusOs, { label: string; className: string; icon: typeof Clock }> = {
  aberta:        { label: 'Aberta',       className: 'bg-blue-500/10 text-blue-500',     icon: Clock        },
  em_andamento:  { label: 'Em Andamento', className: 'bg-yellow-500/10 text-yellow-500', icon: PlayCircle   },
  concluida:     { label: 'Concluída',    className: 'bg-green-500/10 text-green-500',   icon: CheckCircle2 },
  cancelada:     { label: 'Cancelada',    className: 'bg-red-500/10 text-red-500',       icon: CheckCircle2 },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Componente ────────────────────────────────────────────────────────────

const OrdemServico = () => {
  const navigate = useNavigate();
  const { statusCombo } = useStatusOsCombo();

  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [statusOs,      setStatusOs]      = useState('');
  const [prioridade,    setPrioridade]    = useState('');
  const [clienteId,     setClienteId]     = useState(0);
  const [vendedorId,    setVendedorId]    = useState(0);
  const [tecnicoId,     setTecnicoId]     = useState(0);
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'cliente' | 'valor' | 'prioridade'>('emissao', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const filtros = {
    busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
    clienteId, vendedorId, tecnicoId, statusOs,
    prioridade: prioridade === '' ? undefined : Number(prioridade),
    ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
  };

  const { ordens, total, totalPaginas, resumo, isLoading, isFetching, deleteOs } =
    useOrdemServico({ pagina, tamanhoPagina, filtros });

  // ── KPIs (do servidor: filtro inteiro sem o status, não só a página) ──────

  const totalAbertas      = resumo?.abertas      ?? 0;
  const totalEmAndamento  = resumo?.emAndamento  ?? 0;
  const totalConcluidas   = resumo?.concluidas   ?? 0;
  const valorTotalAbertos = resumo?.valorAbertas ?? 0;

  // ── Paginação ──────────────────────────────────────────────────────────

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Ordens de Serviço (OS)</h1>
          <p className="text-muted-foreground mt-1">Gerencie as ordens de serviço da empresa</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/os/novo')}>
          <Plus className="h-4 w-4" />
          Nova OS
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalAbertas}</div>
            <p className="text-xs text-muted-foreground mt-1">{BRL(valorTotalAbertos)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PlayCircle className="h-4 w-4" />Em Andamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{totalEmAndamento}</div>
            <p className="text-xs text-muted-foreground mt-1">Em execução</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Concluídas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalConcluidas}</div>
            <p className="text-xs text-muted-foreground mt-1">Finalizadas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wrench className="h-4 w-4" />Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">ordens no filtro</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Ordens de Serviço</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº OS, cliente ou CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Abertura" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberta">Aberta</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluida">Concluída</SelectItem>
                </SelectContent>
              </Select>
              {statusCombo.length > 0 && (
                <Select
                  value={statusOs || '__todos__'}
                  onValueChange={(v) => { setStatusOs(v === '__todos__' ? '' : v); setPagina(1); }}
                >
                  <SelectTrigger className="w-full sm:w-48" aria-label="Status do cadastro">
                    <SelectValue placeholder="Status do cadastro" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__todos__">Todos os status do cadastro</SelectItem>
                    {statusCombo.map((st) => (
                      <SelectItem key={st.id} value={st.descricao}>{st.descricao}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select
                value={prioridade || '__todas__'}
                onValueChange={(v) => { setPrioridade(v === '__todas__' ? '' : v); setPagina(1); }}
              >
                <SelectTrigger className="w-full sm:w-40" aria-label="Prioridade">
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todas__">Todas as prioridades</SelectItem>
                  <SelectItem value="0">Alta</SelectItem>
                  <SelectItem value="1">Normal</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-full sm:w-60">
                <PessoaLookupCombobox
                  papel="cliente"
                  value={clienteId || null}
                  onChange={(id) => { setClienteId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os clientes"
                />
              </div>
              <div className="w-full sm:w-56">
                <VendedorSearchCombobox
                  value={vendedorId || null}
                  onChange={(id) => { setVendedorId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os vendedores"
                />
              </div>
              <div className="w-full sm:w-56">
                <ColaboradorSearchCombobox
                  value={tecnicoId || null}
                  onChange={(id) => { setTecnicoId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os técnicos"
                />
              </div>
              <BotaoExportar endpoint="/api/os/exportar" params={filtros} />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº OS</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="cliente" ordenacao={ordenacao} decrescentePadrao={false}>Cliente</CabecalhoOrdenavel>
                  <TableHead>Vendedor</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Abertura</CabecalhoOrdenavel>
                  <TableHead>Agendamento</TableHead>
                  <CabecalhoOrdenavel coluna="prioridade" ordenacao={ordenacao} decrescentePadrao={false}>Prioridade</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Total Líquido</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : ordens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhuma ordem de serviço neste período — amplie o período para ver as anteriores'
                        : 'Nenhuma ordem de serviço encontrada'}
                    </TableCell>
                  </TableRow>
                ) : (
                  ordens.map((os) => {
                    const status = calcularStatusOs(os.isBaixada, os.isPedidoEntregue);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    return (
                      <TableRow key={os.id}>
                        <TableCell className="font-medium">#{os.numeroDav}</TableCell>
                        <TableCell>
                          {os.nomeRazao || <span className="text-muted-foreground">—</span>}
                          {os.cpfCnpj && <div className="text-xs text-muted-foreground">{os.cpfCnpj}</div>}
                        </TableCell>
                        <TableCell>
                          {os.vendedorNome || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {new Date(os.dtEmissao).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          {os.dtAgendamento
                            ? new Date(os.dtAgendamento).toLocaleDateString('pt-BR')
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {os.prioridade === 0
                            ? <Badge className="bg-red-500/10 text-red-500">Alta</Badge>
                            : <span className="text-muted-foreground text-sm">Normal</span>}
                        </TableCell>
                        <TableCell className="text-right font-medium">{BRL(os.totalLiquido ?? 0)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            <Badge className={cfg.className}>
                              <span className="flex items-center gap-1">
                                <Icon className="h-3 w-3" />
                                {cfg.label}
                              </span>
                            </Badge>
                            {/* Status do cadastro (último acompanhamento), com a cor cadastrada */}
                            {os.statusAtual && (
                              <Badge
                                variant="outline"
                                style={os.statusCor ? { borderColor: os.statusCor, color: os.statusCor } : undefined}
                              >
                                {os.statusAtual}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {!os.isBaixada && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/os/${os.id}`)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              title="Ver detalhes"
                              onClick={() => navigate(`/operacional/os/${os.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!os.isBaixada && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setDeletingId(os.id)}
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Rodapé de paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && (
                <span className="ml-2 text-xs">(atualizando...)</span>
              )}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  Itens por página:
                </span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(1)}
                  disabled={pagina === 1}
                >
                  «
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p - 1)}
                  disabled={pagina === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button
                      key={page}
                      variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs"
                      onClick={() => setPagina(page)}
                    >
                      {page}
                    </Button>
                  );
                })}

                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p + 1)}
                  disabled={pagina >= totalPaginas}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(totalPaginas)}
                  disabled={pagina >= totalPaginas}
                >
                  »
                </Button>
              </div>

              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Página {pagina} de {totalPaginas}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => {
          if (deletingId) {
            deleteOs(deletingId);
            setDeletingId(null);
          }
        }}
        title="Excluir Ordem de Serviço"
        description="Tem certeza que deseja excluir esta OS? Ordens já baixadas não podem ser excluídas."
      />
    </div>
  );
};

export default OrdemServico;
